import { forwardRef, useId, useLayoutEffect, useState } from 'react'
import { ImageBlock, type CommonBlockProps } from '../../../types'
import { useMessageContext } from '../../../contexts'
import { getImageSize } from '../../../utils'
import { webchatClasses } from '../../../styles/classes'
import { useAsync } from 'react-use'
import { ImageOff } from 'lucide-react'

type Props = ImageBlock & CommonBlockProps
export const Image = forwardRef<HTMLImageElement, Props>(({ url, type, orientation = 'auto', ...props }, ref) => {
  const id = useId()
  const { setIsLoading } = useMessageContext()
  const [imageOrientation, setImageOrientation] = useState(orientation)
  const [loaded, setLoaded] = useState(false)
  const [hasLoadError, setHasLoadError] = useState(false)

  const {
    message: {
      blocks: { image },
    },
  } = webchatClasses

  useLayoutEffect(() => {
    setIsLoading((prev) => [...prev, id])
  }, [])

  useAsync(async () => {
    if (!url) return

    try {
      const { width, height } = await getImageSize(url)
      if (imageOrientation === 'auto') {
        setImageOrientation(getClosestAspectRatio(width, height))
      }
    } catch (error) {
      setImageOrientation('square')
      console.error(error)
    }
    setIsLoading((prev) => {
      return prev.filter((currId) => currId !== id)
    })
  }, [url])

  return (
    <>
      {loaded ? null : <div data-orientation={imageOrientation} {...image.placeholder} />}
      {hasLoadError ? (
        <div data-error="true" data-orientation={imageOrientation} {...props} {...image.image} ref={ref}>
          <ImageOff size={'25%'} strokeWidth={'5%'} />
        </div>
      ) : (
        <img
          data-orientation={imageOrientation}
          data-loaded={loaded}
          {...props}
          {...image.image}
          src={url}
          alt=""
          ref={ref}
          loading="lazy"
          onLoad={() => {
            setLoaded(true)
          }}
          onError={() => {
            setHasLoadError(true)
            setLoaded(true)
          }}
        />
      )}
    </>
  )
})

Image.displayName = 'Image'

function getClosestAspectRatio(width: number, height: number) {
  type aspects = keyof typeof aspects
  const aspects = {
    square: 1,
    portrait: 3 / 4,
    landscape: 4 / 3,
  } as const
  const aspect = width / height
  const closest = (Object.keys(aspects) as aspects[]).reduce((prev, curr) => {
    return Math.abs(aspects[curr] - aspect) < Math.abs(aspects[prev] - aspect) ? curr : prev
  })
  return closest
}
